import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import styles from "./media-card.module.scss";
import Trimmer from "./Trimmer";
import { createScreenshot } from "../../utils";
import CanvasOverlayRenderer from "./CanvasOverlayRenderer";
import { CLEO_PLAYER_API_KEY } from "@/constants";

declare global {
  interface Window {
    CleoPlayer?: any;
    player?: any;
  }
}

/**
 * Sprite-sheet thumbnail config from the player's remoteConfig. Frames are
 * packed `rc` columns × `rr` rows per sheet; sheets are named `<url><n>.<ext>`.
 * `tc[i]` is the timecode (seconds) of frame `i`.
 */
export interface ImageReel {
  url: string;
  tc: number[];
  tw: number;
  th: number;
  rc: number;
  rr: number;
  ext: string[];
}

interface PlayerProps {
  id: string;
  showTrimmer?: boolean;
  controls?: boolean;
  /** Fires once the sprite/imagereel config is available on the player. */
  onImageReel?: (reel: ImageReel) => void;
  startTrim?: number;
  endTrim?: number;
  onTrimChange?: (startTrim: number, endTrim: number) => void;
  remoteConfig?: boolean;
  playerType?: string;
  videoData?: Record<string, string>;
  autoPlay?: number;
  thumbSent?: string;
  embedUpdateKey?: any;
  isSimulive?: boolean;
  onScreenshot?: (blobURL: string, base64Img: string) => void;
  onTimeUpdate?: (time: number) => void;
  onPlayStateChange?: (isPaused: boolean) => void;
  allowMultipleSegments?: boolean;
  videos?: Array<{
    id: string;
    title: string;
    duration: number;
  }>;
  onSegmentsChange?: (segments: any[]) => void;
  showCanvasOverlay?: boolean;
  textOverlays?: Array<{
    id: string;
    text: string;
    fontSize: number;
    fontColor: string;
    backgroundColor: string;
    position: { x: number; y: number };
    size?: { width: number; height: number };
    visible: boolean;
  }>;
  logoOverlays?: Array<{
    id: string;
    url: string;
    position: { x: number; y: number };
    size: { width: number; height: number };
    visible: boolean;
  }>;

  slateOverlays?: Array<{
    id: string;
    url: string;
    position: { x: number; y: number };
    size: { width: number; height: number };
    visible: boolean;
    isSlate: boolean;
    duration?: number;
    autoHide?: boolean;
    isPreRoll?: boolean;
    isPostRoll?: boolean;
  }>;
  onOverlayChange?: (overlayData: any) => void;
  onTextOverlaySelect?: (textOverlayId: string) => void;
  onLogoOverlaySelect?: (logoOverlayId: string) => void;
  selectedTextOverlayId?: string;
  selectedLogoOverlayId?: string;
  onCloseLogoSliders?: () => void;
  externalShowTextModal?: boolean;
  externalShowLogoModal?: boolean;
  externalTextEditingState?: any;
  externalLogoEditingState?: any;
  onHighResCapture?: (canvas: HTMLCanvasElement) => void;
  videoDimensions?: { width: number; height: number };
  preRollSlateId?: string;
  postRollSlateId?: string;
  maintainAspectRatio?: boolean;
  onSequentialPlayback?: (playbackSequence: {
    type: "pre-slate" | "video-segments" | "post-slate";
    slateId?: string;
    segments?: Array<{ start: number; end: number }>;
    reset?: boolean;
  }) => void;
  onReset?: () => void;
  duration?: number;
  isPreset?: boolean;
  canvasOverlay?: any;
  preRollMedia?: any;
  postRollMedia?: any;
  item?: any;
  getTotalDuration?: (totalDuration: number) => void;
  videoUrl?: string;
  readOnlyMode?: boolean;
}

export interface GeneralPlayerHandle {
  play: () => void;
  pause: () => void;
  seek: (time: number) => void;
  getCurrentTime: () => number;
  isPaused: () => boolean;
}

export const GeneralPlayer = forwardRef<GeneralPlayerHandle, PlayerProps>(
  (
    {
      id,
      videoUrl,
      controls: controlsProp,
      onImageReel,
      showTrimmer = false,
      startTrim = 0,
      endTrim = 0,
      onTrimChange,
      remoteConfig = false,
      playerType,
      videoData,
      autoPlay = 2,
      thumbSent,
      embedUpdateKey,
      isSimulive = false,
      onScreenshot,
      onTimeUpdate,
      onPlayStateChange,
      showCanvasOverlay = false,
      textOverlays,
      logoOverlays,
      slateOverlays,
      allowMultipleSegments = false,
      onOverlayChange,
      onTextOverlaySelect,
      onLogoOverlaySelect,
      selectedTextOverlayId,
      selectedLogoOverlayId,
      onCloseLogoSliders,
      externalShowTextModal = false,
      externalShowLogoModal = false,
      externalTextEditingState,
      externalLogoEditingState,
      onHighResCapture,
      onSegmentsChange,
      videoDimensions, // New prop
      preRollSlateId,
      postRollSlateId,
      maintainAspectRatio = false,
      onSequentialPlayback,
      onReset,
      duration,
      isPreset = false,
      canvasOverlay,
      preRollMedia,
      postRollMedia,
      item,
      getTotalDuration,
      readOnlyMode = false,
    }: PlayerProps,
    ref
  ) => {
    const playerRef = useRef<any>(null);
    const contRef = useRef<any>(null);
    const videoDataRef = useRef(videoData);
    const [pauseIcon, setPauseIcon] = useState(false);
    const [showScreenshotIcon, setShowScreenshotIcon] = useState(false);

    useImperativeHandle(ref, () => ({
      play: () => {
        playerRef.current?.play();
        setPauseIcon(false);
      },
      pause: () => {
        playerRef.current?.pause();
        setPauseIcon(true);
      },
      seek: (time: number) => {
        playerRef.current?.seek(time);
      },
      getCurrentTime: () => playerRef.current?.cPlayer?.video?.currentTime ?? 0,
      isPaused: () => pauseIcon,
    }));

    useEffect(() => {
      onPlayStateChange?.(pauseIcon);
    }, [pauseIcon, onPlayStateChange]);

    const hasUserDragged = useRef(false);
    const [currentVideoId, setCurrentVideoId] = useState(id);
    const [currentPlaybackPhase, setCurrentPlaybackPhase] = useState<
      "pre-slate" | "video-segments" | "post-slate" | null
    >(null);
    const [isSequentialPlaybackActive, setIsSequentialPlaybackActive] =
      useState(false);
    const [currentSegments, setCurrentSegments] = useState<
      Array<{ start: number; end: number }>
    >([]);
    const [isMuted, setIsMuted] = useState(false);
    const [state, setState] = useState({
      playedDuration: 0,
      totalDuration: duration || 0,
      trimStart: startTrim,
      trimEnd: endTrim,
    });
    const originalVideoDurationRef = useRef(0);
    const [localTextOverlays, setLocalTextOverlays] = useState(
      textOverlays || [
        {
          id: `text-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          text: "",
          fontSize: 24,
          fontColor: "#ffffff",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          position: { x: 50, y: 50 },
          size: { width: 200, height: 50 },
          visible: false,
        },
      ]
    );
    const [localLogoOverlays, setLocalLogoOverlays] = useState(
      logoOverlays || []
    );
    const [localSlateOverlays, setLocalSlateOverlays] = useState(
      slateOverlays || []
    );

    const canvasSlateOverlays = localSlateOverlays.filter(
      (slate) => !slate?.isPreRoll && !slate?.isPostRoll
    );

    useEffect(() => {
      if (textOverlays) {
        setLocalTextOverlays(textOverlays);
      }
    }, [textOverlays]);

    useEffect(() => {
      if (logoOverlays) {
        setLocalLogoOverlays(logoOverlays);
      }
    }, [logoOverlays]);

    useEffect(() => {
      if (slateOverlays) {
        setLocalSlateOverlays(slateOverlays);
      }
    }, [slateOverlays]);

    const handleTextOverlaysChange = (newTextOverlays: any[]) => {
      setLocalTextOverlays(newTextOverlays);
      onOverlayChange?.({
        textOverlays: newTextOverlays,
        logoOverlays: localLogoOverlays,
        slateOverlays: slateOverlays || [],
      });
    };

    const handleLogoOverlaysChange = (newLogoOverlays: any[]) => {
      setLocalLogoOverlays(newLogoOverlays);
      onOverlayChange?.({
        textOverlays: localTextOverlays,
        logoOverlays: newLogoOverlays,
        slateOverlays: slateOverlays || [],
      });
    };

    const handleMute = () => {
      setIsMuted(!isMuted);
    };

    const handleSlateOverlaysChange = (newSlateOverlays: any[]) => {
      setLocalSlateOverlays(newSlateOverlays);
      onOverlayChange?.({
        textOverlays: localTextOverlays,
        logoOverlays: localLogoOverlays,
        slateOverlays: newSlateOverlays,
      });
    };

    useEffect(() => {
      setState((prev) => ({ ...prev, trimStart: startTrim, trimEnd: endTrim }));
    }, [startTrim, endTrim]);

    useEffect(() => {
      destroyPlayer();
      if (contRef.current) {
        loadPlayer();
      }
      return () => {
        destroyPlayer();
      };
    }, [embedUpdateKey, thumbSent, currentVideoId, videoData?.mid, isSimulive]);

    useEffect(() => {
      if (playerRef.current) {
        if (isMuted) {
          playerRef.current.mute();
        } else {
          playerRef.current.unmute();
        }

        const videoElement = playerRef.current?.cPlayer?.video;
        if (videoElement) {
          videoElement.muted = isMuted;
        }
      }
    }, [isMuted]);

    useEffect(() => {
      const embededKey = { mid: embedUpdateKey };
      videoDataRef.current = embededKey;
    }, [embedUpdateKey]);

    function onMediaData(id: any, callback: any) {
      const mediaData = {
        playerType: playerType || "native",
        video: {
          ...videoDataRef.current,
          url: videoUrl,
        },
        isLive: false,
      };
      callback && callback(200, null, mediaData);
    }

    const getControlsStatus = (): boolean => {
      // Explicit prop wins over the default derivation.
      if (controlsProp !== undefined) {
        return controlsProp;
      }

      if (showTrimmer) {
        return false;
      }

      if (playerType && playerType === "fb") {
        return false;
      }

      return true;
    };

    const getPlayerConfig = () => ({
      id: !remoteConfig ? currentVideoId : "test",
      autoPlay: isSequentialPlaybackActive ? 1 : autoPlay,
      apiKey: CLEO_PLAYER_API_KEY,
      el: contRef.current,
      mute: isMuted ?? false,
      controls: getControlsStatus(),
      thumb: thumbSent,
      skipModule: {
        skipAd: true,
        skipAnalytics: true,
      },
      callbacks: {
        onPlayerConfig: () => ({
          player: {
            bgPause: false,
            loop:
              !isSequentialPlaybackActive && startTrim === 0 && endTrim === 0,
            fgResume: false,
          },
        }),
        onMediaData: remoteConfig ? onMediaData : null,
      },
      internals: {
        mediaBase: `${process.env.NX_APP_MEDIA_BASE_URL ?? ""}/`,
      },
    });

    const loadPlayer = () => {
      playerRef.current = new window.CleoPlayer(
        getPlayerConfig(),
        (error: string) => {
          if (error) {
            return;
          }
          bindPlayerEvents();
          setPauseIcon(true);
        }
      );
      playerRef.current.on(
        window.CleoPlayer?.Events?.LOAD_ERROR,
        (eventName: string, obj: any) => {
          console.error(eventName, obj);
        }
      );
      window.player = playerRef.current;
    };

    const destroyPlayer = () => {
      try {
        unBindPLayerEvents();
        if (playerRef.current?.state) {
          playerRef.current.pause();
        }
        if (playerRef.current?.destroy) {
          playerRef.current.destroy();
        }
      } catch (error) {
        console.warn("Error destroying player:", error);
      } finally {
        playerRef.current = null;
      }
    };

    const handlePlayerReady = () => {
      if (autoPlay === 2) {
        console.warn("handlePlayerReady called", autoPlay);

        playerRef.current?.play();
      }
    };

    const bindPlayerEvents = () => {
      const CleoPlayer = window.CleoPlayer;
      playerRef.current?.on(
        CleoPlayer.Events.MEDIA_ATTACHED,
        handleVideoLoaded
      );
      playerRef.current?.on(CleoPlayer.Events.MEDIA_READY, handlePlayerReady);
      playerRef.current?.on(
        CleoPlayer.Events.VIDEO_TIMEUPDATE,
        handleVideoTimeUpdate
      );
      playerRef.current?.on(
        CleoPlayer.Events.VIDEO_STARTED,
        handleVideoStarted
      );
      playerRef.current?.on(CleoPlayer.Events.VIDEO_PAUSED, handleVideoPaused);
      playerRef.current?.on(
        CleoPlayer.Events.VIDEO_RESUMED,
        handleVideoResumed
      );
      playerRef.current?.on(CleoPlayer.Events.VIDEO_ENDED, handleVideoEnded);

      const videoElement = playerRef.current?.cPlayer?.video;
      if (videoElement && startTrim > 0) {
        const handleLoadedData = () => {
          videoElement.currentTime = startTrim;
          playerRef.current?.pause();
          setPauseIcon(true);
          videoElement.removeEventListener("loadeddata", handleLoadedData);
        };

        const handleCanPlay = () => {
          videoElement.currentTime = startTrim;
          playerRef.current?.pause();
          setPauseIcon(true);
          videoElement.removeEventListener("canplay", handleCanPlay);
        };

        videoElement.addEventListener("loadeddata", handleLoadedData);
        videoElement.addEventListener("canplay", handleCanPlay);
      }
    };

    const unBindPLayerEvents = () => {
      const CleoPlayer = window.CleoPlayer;
      playerRef.current?.removeListener(
        CleoPlayer.Events.VIDEO_ENDED,
        handleVideoEnded
      );
      playerRef.current?.removeListener(
        CleoPlayer.Events.MEDIA_ATTACHED,
        handleVideoLoaded
      );
      playerRef.current?.removeListener(
        CleoPlayer.Events.VIDEO_TIMEUPDATE,
        handleVideoTimeUpdate
      );
      playerRef.current?.removeListener(
        CleoPlayer.Events.VIDEO_STARTED,
        handleVideoStarted
      );
      playerRef.current?.removeListener(
        CleoPlayer.Events.VIDEO_PAUSED,
        handleVideoPaused
      );
      playerRef.current?.removeListener(
        CleoPlayer.Events.VIDEO_RESUMED,
        handleVideoResumed
      );
      // playerRef.current?.removeListener(CleoPlayer.Events.MEDIA_READY, handlePlayerReady);
    };

    const handleVideoStarted = () => {
      playerRef.current?.ui.setLoader(0);
      setPauseIcon(false);
    };

    const handleVideoPaused = () => {
      playerRef.current?.ui.setLoader(0);
      setPauseIcon(true);
    };

    const handleVideoResumed = () => {
      playerRef.current?.ui.setLoader(0);
      setPauseIcon(false);
    };

    const handleVideoTimeUpdate = (data: any, time: number) => {
      const total: number =
        playerRef.current?.cPlayer?.video?.duration?.toFixed(3);

      if (currentPlaybackPhase === "video-segments") {
        setState((prev) => ({
          ...prev,
          playedDuration: time,
          totalDuration: total,
        }));
        onTimeUpdate?.(time);
      } else if (
        currentPlaybackPhase === "pre-slate" ||
        currentPlaybackPhase === "post-slate"
      ) {
        setState((prev) => ({
          ...prev,
          playedDuration: prev.playedDuration, // Keep playedDuration frozen during slates
          totalDuration: originalVideoDurationRef.current || total,
        }));
      } else {
        if (originalVideoDurationRef.current === 0) {
          originalVideoDurationRef.current = total;
        }
        setState((prev) => ({
          ...prev,
          totalDuration: total,
          playedDuration: time,
        }));
        onTimeUpdate?.(time);
        if (
          state.trimEnd &&
          time >= state.trimEnd &&
          hasUserDragged.current === false
        ) {
          playerRef.current?.pause();
          setPauseIcon(true);
          playerRef.current?.seek(state.trimStart);
        }
      }
    };

    const handleVideoLoaded = () => {
      setTimeout(() => {
        const total: number =
          playerRef.current?.cPlayer?.video?.duration?.toFixed(3);

        if (originalVideoDurationRef.current === 0) {
          originalVideoDurationRef.current = total;
        }

        // Surface the sprite/imagereel thumbnail config once it's available.
        const reel = playerRef.current?.store?.config?.remoteConfig?.imagereel;
        if (reel?.url && Array.isArray(reel?.tc)) {
          onImageReel?.(reel);
        }

        const effectiveDuration = duration || total;
        const effectiveEndTrim = endTrim || effectiveDuration;

        setState((prev) => ({
          ...prev,
          totalDuration: effectiveDuration,
          trimEnd: effectiveEndTrim,
        }));

        if (isSequentialPlaybackActive) {
          setState((prev) => ({ ...prev, playedDuration: 0 }));
        }

        if (isSequentialPlaybackActive) {
          if (currentPlaybackPhase === "video-segments") {
            // Let the Trimmer handle everything for video-segments
          } else {
            playerRef.current?.play();
            setPauseIcon(false);
          }
        } else {
          if (startTrim > 0) {
            try {
              playerRef.current?.seek(startTrim);
            } catch (error) {
              console.error("Immediate seek failed:", error);
            }
            const videoElement = playerRef.current?.cPlayer?.video;
            if (videoElement) {
              videoElement.currentTime = startTrim;
            }

            setTimeout(() => {
              const currentTime =
                playerRef.current?.cPlayer?.video?.currentTime || 0;
              if (Math.abs(currentTime - startTrim) > 1) {
                setTimeout(() => {
                  playerRef.current?.seek(startTrim);
                  if (videoElement) {
                    videoElement.currentTime = startTrim;
                  }
                }, 500);
              }
            }, 200);
          } else if (autoPlay === 2) {
            playerRef.current?.play();
            setPauseIcon(false);
          } else {
            playerRef.current?.pause();
            setPauseIcon(true);
          }
        }
      }, 100);
    };

    useEffect(() => {
      if (isSequentialPlaybackActive && playerRef.current) {
        if (
          currentPlaybackPhase === "pre-slate" ||
          currentPlaybackPhase === "post-slate"
        ) {
          playerRef.current?.play();
          setPauseIcon(false);
        } else if (currentPlaybackPhase === "video-segments") {
          playerRef.current?.play();
          setPauseIcon(false);
        }
      }
    }, [isSequentialPlaybackActive, currentPlaybackPhase]);

    const handleVideoEnded = () => {
      if (currentPlaybackPhase === "post-slate") {
        setIsSequentialPlaybackActive(false);
        setCurrentPlaybackPhase(null);
        setCurrentVideoId(id);
        setCurrentSegments([]);
      } else {
        playerRef.current.store.state.playing = true;
        playerRef.current?.rePlay(false);
        playerRef.current?.pause();
        setPauseIcon(true);
      }
    };

    const onTrimUpdate = (startTrim: number, endTrim: number) => {
      setState((prev) => ({ ...prev, trimStart: startTrim, trimEnd: endTrim }));
      onTrimChange?.(startTrim, endTrim);
    };

    const handleScreenshot = () => {
      if (onScreenshot) {
        const videoTag = document.getElementsByTagName(
          "video"
        )[0] as HTMLVideoElement;
        if (videoTag) {
          createScreenshot(videoTag).then(({ blobURL, base64Img }) => {
            onScreenshot(blobURL, base64Img);
          });
        }
      }
    };

    const handleSequentialPlayback = (playbackSequence: {
      type: "pre-slate" | "video-segments" | "post-slate";
      slateId?: string;
      segments?: Array<{ start: number; end: number }>;
      reset?: boolean;
    }) => {
      if (playbackSequence.reset) {
        setIsSequentialPlaybackActive(false);
        setCurrentPlaybackPhase(null);
        setCurrentVideoId(id);
        setCurrentSegments([]);
        setState((prev) => ({
          ...prev,
          playedDuration: 0,
          totalDuration: duration || originalVideoDurationRef.current,
          trimEnd: endTrim || duration || originalVideoDurationRef.current,
        }));
        return;
      }

      if (
        playbackSequence.type === "pre-slate" ||
        playbackSequence.type === "video-segments"
      ) {
        setIsSequentialPlaybackActive(true);
      }

      if (playbackSequence.type === "pre-slate" && playbackSequence.slateId) {
        setCurrentPlaybackPhase("pre-slate");
        setCurrentVideoId(playbackSequence.slateId);
      } else if (playbackSequence.type === "video-segments") {
        setCurrentPlaybackPhase("video-segments");
        setCurrentVideoId(id);

        if (playbackSequence.segments) {
          setCurrentSegments(playbackSequence.segments);
        }
        setTimeout(() => {
          if (playerRef.current && currentPlaybackPhase === "video-segments") {
            playerRef.current?.play();
            setPauseIcon(false);
          }
        }, 200);
      } else if (
        playbackSequence.type === "post-slate" &&
        playbackSequence.slateId
      ) {
        setCurrentPlaybackPhase("post-slate");
        setCurrentVideoId(playbackSequence.slateId);
      }
      if (playbackSequence.type === "post-slate") {
        // The state will be reset when the post-slate video ends
      }
      onSequentialPlayback?.(playbackSequence);
    };

    return (
      <div
        className={styles.videoItemPlayer}
        onMouseEnter={() => setShowScreenshotIcon(true)}
        onMouseLeave={() => setShowScreenshotIcon(false)}
      >
        <div className={`${styles.playerRatio} ratio ratio-16x9`} ref={contRef}>
          <CanvasOverlayRenderer
            showCanvasOverlay={showCanvasOverlay}
            textOverlays={localTextOverlays}
            logoOverlays={localLogoOverlays}
            slateOverlays={canvasSlateOverlays}
            onTextOverlaysChange={handleTextOverlaysChange}
            onLogoOverlaysChange={handleLogoOverlaysChange}
            onSlateOverlaysChange={handleSlateOverlaysChange}
            onTextOverlaySelect={onTextOverlaySelect}
            onLogoOverlaySelect={onLogoOverlaySelect}
            selectedTextOverlayId={selectedTextOverlayId}
            selectedLogoOverlayId={selectedLogoOverlayId}
            onCloseLogoSliders={onCloseLogoSliders}
            externalShowTextModal={externalShowTextModal}
            externalShowLogoModal={externalShowLogoModal}
            externalTextEditingState={externalTextEditingState}
            externalLogoEditingState={externalLogoEditingState}
            onOverlayChange={onOverlayChange}
            onHighResCapture={onHighResCapture}
            videoDimensions={videoDimensions}
            isPreset={isPreset}
            contentNonEditable={isPreset}
            readOnlyMode={readOnlyMode}
            maintainAspectRatio={maintainAspectRatio}
          />
        </div>
        {showTrimmer && (
          <Trimmer
            trimStart={startTrim}
            trimEnd={endTrim}
            playerRef={playerRef}
            playedDuration={state.playedDuration}
            totalDuration={state.totalDuration}
            OnTrim={onTrimUpdate}
            pauseIcon={pauseIcon}
            setPauseIcon={setPauseIcon}
            setHasUserDragged={hasUserDragged}
            allowMultipleSegments={allowMultipleSegments}
            onSegmentsChange={onSegmentsChange}
            slateOverlays={slateOverlays}
            onSlateOverlaysChange={handleSlateOverlaysChange}
            preRollSlateId={preRollSlateId}
            postRollSlateId={postRollSlateId}
            onSequentialPlayback={handleSequentialPlayback}
            originalVideoDuration={originalVideoDurationRef.current}
            onReset={onReset}
            isPreset={isPreset}
            duration={duration}
            canvasOverlay={canvasOverlay}
            preRollMedia={preRollMedia}
            postRollMedia={postRollMedia}
            item={item}
            getTotalDuration={getTotalDuration}
            isMuted={isMuted}
            handleMute={handleMute}
            readOnlyMode={readOnlyMode}
          />
        )}
        {showScreenshotIcon && onScreenshot && (
          <button
            type="button"
            className={styles.screenshotIcon}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleScreenshot();
            }}
            style={{ pointerEvents: "auto" }}
          >
            <i className="bi bi-camera"></i>
          </button>
        )}
      </div>
    );
  }
);

GeneralPlayer.displayName = "GeneralPlayer";
